// Authentication API Service

import { AuthResponse, LoginCredentials, User } from '../../types/admin';

// In production, use relative URLs (same domain). In dev, fallback to localhost.
const API_BASE = process.env.NODE_ENV === 'production'
  ? ''
  : (process.env.REACT_APP_API_URL || 'http://localhost:8000');

class AuthApiService {
  private baseUrl = `${API_BASE}/api/auth`;

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await fetch(`${this.baseUrl}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Login failed' }));
      throw new Error(error.detail || 'Login failed');
    }

    return response.json();
  }

  async logout(): Promise<void> {
    const token = this.getToken();
    if (!token) return;

    try {
      await fetch(`${this.baseUrl}/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
    } finally {
      this.clearToken();
    }
  }

  async getCurrentUser(): Promise<User> {
    const token = this.getToken();
    if (!token) {
      throw new Error('No token available');
    }

    const response = await fetch(`${this.baseUrl}/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.clearToken();
        throw new Error('Session expired');
      }
      throw new Error('Failed to get user data');
    }

    return response.json();
  }

  async refreshToken(): Promise<AuthResponse> {
    const token = this.getToken();
    if (!token) {
      throw new Error('No token to refresh');
    }

    const response = await fetch(`${this.baseUrl}/refresh`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      this.clearToken();
      throw new Error('Token refresh failed');
    }

    const data = await response.json();
    this.setToken(data.access_token);
    return data;
  }

  // Token management
  getToken(): string | null {
    return localStorage.getItem('admin_token');
  }

  setToken(token: string): void {
    localStorage.setItem('admin_token', token);
  }

  clearToken(): void {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  }

  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= exp;
    } catch {
      return true;
    }
  }

  // User management
  getStoredUser(): User | null {
    const userStr = localStorage.getItem('admin_user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  setStoredUser(user: User): void {
    localStorage.setItem('admin_user', JSON.stringify(user));
  }
}

export const authApi = new AuthApiService();
export default authApi;
